import { useState } from "react";
import { api } from "../services/api/api";

type MediaItem = EasyTech.Photo | EasyTech.WorkorderPhoto | EasyTech.WorkorderSignature;

interface UseMediaOptions {
  workorderId: string;
  initialMedia: MediaItem[];
  endpoint: "photos" | "signatures";
}

export const useMedia = ({
  workorderId,
  initialMedia,
  endpoint,
}: UseMediaOptions) => {
  const [media, setMedia] = useState<MediaItem[]>(initialMedia);
  const [loadingType, setLoadingType] = useState<"BEFORE" | "AFTER" | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const getDownloadUrl = (mediaId: string) => {
    return `users/workorders/${workorderId}/${endpoint}/${mediaId}/download`;
  };

  const reloadMedia = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`users/workorders/${workorderId}/${endpoint}`);
      const mediaData = Array.isArray(response) ? response : [];
      setMedia(mediaData.sort((a, b) => b.id.localeCompare(a.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reload media");
      console.error("Error reloading media:", err);
    } finally {
      setLoading(false);
    }
  };
  const uploadMedia = async (
    file: File,
    type: "BEFORE" | "AFTER",
    additionalData?: Record<string, string>
  ) => {
    setLoadingType(type);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, value);
        });
      }

      const response = await api.post(
        `users/workorders/${workorderId}/${endpoint}/${type.toLowerCase()}`,
        formData,
        { isMultipart: true }
      );

      const newMedia = response as MediaItem;

      // Recharger toutes les données depuis le serveur pour s'assurer de la synchronisation
      await reloadMedia();

      return newMedia;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      throw err;
    } finally {
      setLoadingType(null);
    }
  };

  const deleteMedia = async (mediaId: string) => {
    try {
      await api.delete(
        `users/workorders/${workorderId}/${endpoint}/${mediaId}`
      );

      // Recharger toutes les données depuis le serveur pour s'assurer de la synchronisation
      await reloadMedia();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
      throw err;
    }
  };

  return {
    media,
    loadingType,
    error,
    loading,
    uploadMedia,
    deleteMedia,
    reloadMedia,
    setMedia,
    getDownloadUrl,
  };
};
